import { datadogRum } from '@datadog/browser-rum';
import { isWorker } from '@frameio/utils/config/helpers';
import { log } from '@frameio/utils/log';
import type { AnalyticsEvent } from './events/analytics';
import { WORKER_EMIT_EVENT } from './types';

const MAX_RECENT_TRACKED_EVENTS = 1000;

export type EventContext = Record<string, any>;

export type EventOptions = {
  logLevel?: LogLevel;
};

export type AnalyticsEventOptions = {
  logLevel?: LogLevel;
  apmPayload?: Record<string, any>;
  sendToApm?: boolean;
};

export type EventInstance = {
  id: string;
  addContext: (ctx?: EventContext) => void;
  getContext: () => EventContext;
  completed: (ctx?: EventContext) => void;
  headers: () => any;
  failed: (ctx?: EventContext) => void;
  status: (status: string, ctx?: EventContext) => void;
};

type LogLevel = 'none' | 'info' | 'debug' | 'warn' | 'error';

export const TELEMETRY_EVENT_ID_HEADER = 'x-telemetry-event-id';
export const TELEMETRY_EVENT_NAME_HEADER = 'x-telemetry-event-name';

export function startEvent(
  name: string,
  context: EventContext = {},
  opts: EventOptions = {}
): EventInstance {
  const startTime = performance.now();
  let lastStatusTime = performance.now();

  const evtId = crypto.randomUUID();
  let evtStatus = 'started';
  let evtCtx = {
    id: evtId,
    startedAt: new Date(),
    ...context,
  };

  const evt: EventInstance = {
    id: evtId,
    addContext: (ctx: any) => {
      evtCtx = { ...evtCtx, ...ctx };
    },
    getContext: (): EventContext => {
      const now = performance.now();

      return {
        ...evtCtx,
        id: evtId,
        status: evtStatus,
        time_since_started_milli: now - startTime,
        time_since_last_status_milli: now - lastStatusTime,
      };
    },
    completed: (ctx: EventContext) => {
      evt.status('completed', ctx);
    },
    failed: (ctx: EventContext) => {
      evt.status('failure', ctx);
    },
    headers: () => {
      return {
        [TELEMETRY_EVENT_NAME_HEADER]: name,
        [TELEMETRY_EVENT_ID_HEADER]: evtId,
      };
    },
    status: (status: string, ctx: EventContext) => {
      evtStatus = status;
      evt.addContext(ctx);

      emitEvent(name, evt.getContext(), opts);
      lastStatusTime = performance.now();
    },
  };

  trackRecentEvent(evt);
  return evt;
}

export function emitEvent(
  name: string,
  context: EventContext,
  opts?: EventOptions
) {
  if (isWorker) {
    self.postMessage({
      eventType: WORKER_EMIT_EVENT,
      payload: { name, context, opts },
    });
  } else {
    datadogRum.addAction(name, context);
  }

  // log to console only on `log.error` and `log.warn`:
  const logDatadogAction =
    opts?.logLevel === 'error' || opts?.logLevel === 'warn';

  if (logDatadogAction && !isWorker) {
    const fullName = context.status ? `${name} (${context.status})` : name;
    log[opts.logLevel](`Telemetry Event: ${fullName} - `, context);
  }
}

export function emitAnalyticsEvent(
  event: AnalyticsEvent,
  opts?: AnalyticsEventOptions
) {
  const { name, ...context } = event;
  const { sendToApm = true } = opts || {};

  if (sendToApm) {
    const apmPayload = {
      name,
      status: context.status,
      ...opts?.apmPayload,
      analytics: context,
    };
    emitEvent(event.name, apmPayload, opts);
  }
}

const recentEvents: Array<EventInstance> = [];
const trackRecentEvent = (evt: EventInstance) => {
  recentEvents.unshift(evt);
  if (recentEvents.length > MAX_RECENT_TRACKED_EVENTS) {
    recentEvents.pop();
  }
};

export const getRecentEvent = (id: string): EventInstance | null => {
  for (const evt of recentEvents) {
    if (evt.id === id) return evt;
  }
  return null;
};
